// @ts-nocheck
/* eslint-disable no-unused-vars, no-alert */
import io from 'socket.io-client';
import { eventChannel } from 'redux-saga';
import { takeLatest, spawn, put, call, take, delay, select } from 'redux-saga/effects';
import { t } from 'i18next';

import { truthty } from 'utils/functions';
import { setBaudrate, vendorRead, vendorWrite } from 'utils/services/scaleFnc';
import apiRequest, { apiSuccess, objectToQueryString } from '../../utils/api';

import {
  LOG_OUT,
  SEARCH_PRINTER,
  SEARCH_PRINTER_FAIL,
  SEARCH_PRINTER_SUCCESS,
  PRINT_LABEL,
  PRINT_RECEPTION_LABEL,
  PRINT_LABEL_PAKS,
  appActions,
  ACTIVATE,
  SEARCH_SCALE,
  SEARCH_SCALE_FAIL,
  SEARCH_SCALE_SUCCESS,
  READ_SCALE,
  READ_SCALE_SUCCESS,
  READ_SCALE_FAIL,
  GET_USER,
  GET_USER_SUCCESS,
  GET_USER_FAIL,
  GET_STATE_FROM_API,
  GET_STATE_FROM_API_SUCCESS,
  EXPORT_EXCEL,
  EXPORT_EXCEL_SUCCESS,
  CAPTURE_WEIGHT,
  EDIT_WEIGHT_SCALE,
} from '../reducer/index';

const PRINTER_VENDOR_ID = 2655;
const WEIGHT_SCALE_VENDOR_ID = 1659;
const vendprId2 = 0x067b;
const PDF_CONTINGENCY_MODE_KEY = 'pakit_pdf_contingency_mode';
const DEFAULT_NETWORK_PRINTER_PORT = '9100';
const LOCAL_PRINTER_AGENT_URL = 'http://127.0.0.1:9520';

const getPdfFileName = (prefix) => {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  return `${prefix}-${stamp}.pdf`;
};

const askExportQrPdf = () =>
  window.confirm(
    'No se pudo imprimir la etiqueta. ¿Deseas exportar los QR a PDF para seguir operando?',
  );

const askEnablePdfContingencyMode = () =>
  window.confirm(
    '¿Activar Modo PDF? Mientras esté activo, se exportará PDF directo sin intentar impresora.',
  );

const isPdfContingencyModeEnabled = () =>
  window.localStorage.getItem(PDF_CONTINGENCY_MODE_KEY) === 'true';

const setPdfContingencyMode = (enabled) => {
  window.localStorage.setItem(PDF_CONTINGENCY_MODE_KEY, enabled ? 'true' : 'false');
};

const buildBrowserPrintDevice = (printer) => ({
  ...printer,
  productName: printer?.name || printer?.uid || 'BrowserPrint printer',
  connection: printer?.connection || 'network',
});

const getBrowserPrintDefaultPrinter = () =>
  new Promise((resolve, reject) => {
    if (typeof window.BrowserPrint === 'undefined') {
      reject(new Error('BrowserPrint not available verify'));
      return;
    }

    window.BrowserPrint.getDefaultDevice(
      'printer',
      (printer) => {
        if (printer) {
          resolve(buildBrowserPrintDevice(printer));
          return;
        }

        reject(new Error('No printer found via BrowserPrint'));
      },
      (error) => {
        reject(new Error(error?.message || 'No printer found via BrowserPrint'));
      },
    );
  });

const getBrowserPrintLocalPrinters = () =>
  new Promise((resolve, reject) => {
    if (typeof window.BrowserPrint === 'undefined') {
      reject(new Error('BrowserPrint not available verify'));
      return;
    }

    window.BrowserPrint.getLocalDevices(
      (printers) => {
        resolve(Array.isArray(printers) ? printers.map(buildBrowserPrintDevice) : []);
      },
      (error) => {
        reject(new Error(error?.message || 'No printer found via BrowserPrint'));
      },
      'printer',
    );
  });

const resolveBrowserPrintPrinter = async () => {
  try {
    return await getBrowserPrintDefaultPrinter();
  } catch (defaultError) {
    const printers = await getBrowserPrintLocalPrinters();

    if (!printers.length) {
      throw defaultError;
    }

    return printers[0];
  }
};

const normalizeNetworkPrinterConfig = (config = {}) => ({
  ip: typeof config?.ip === 'string' ? config.ip.trim() : '',
  port: `${config?.port || DEFAULT_NETWORK_PRINTER_PORT}`.trim() || DEFAULT_NETWORK_PRINTER_PORT,
});

const buildNetworkPrinterDevice = (config = {}) => {
  const normalizedConfig = normalizeNetworkPrinterConfig(config);
  return {
    productName: `IP ${normalizedConfig.ip}:${normalizedConfig.port}`,
    connection: 'network-ip',
    ip: normalizedConfig.ip,
    port: normalizedConfig.port,
  };
};

const validateNetworkPrinterConfig = (config = {}) => {
  const normalizedConfig = normalizeNetworkPrinterConfig(config);
  const ipv4Regex =
    /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;

  if (!normalizedConfig.ip) {
    return {
      valid: false,
      message: 'Network printer IP is required',
    };
  }

  if (!ipv4Regex.test(normalizedConfig.ip)) {
    return {
      valid: false,
      message: 'Invalid network printer IP',
    };
  }

  const port = Number(normalizedConfig.port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    return {
      valid: false,
      message: 'Invalid network printer port',
    };
  }

  return {
    valid: true,
    config: {
      ip: normalizedConfig.ip,
      port,
    },
  };
};

const callLocalPrinterAgent = async (path, payload) => {
  if (window.location.protocol === 'https:') {
    return {
      ok: false,
      error: true,
      errorMsg: 'Local printer agent unsupported on HTTPS',
    };
  }

  try {
    const response = await fetch(`${LOCAL_PRINTER_AGENT_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const rawBody = await response.text();
    let json = {};

    try {
      json = rawBody ? JSON.parse(rawBody) : {};
    } catch (parseError) {
      json = {};
    }

    if (!response.ok) {
      return {
        ok: false,
        error: true,
        errorMsg: json.errorMsg || json.message || json.error || rawBody || 'Error in request',
        ...json,
      };
    }

    return {
      ok: true,
      ...json,
    };
  } catch (error) {
    return {
      ok: false,
      error: true,
      errorMsg: 'Local printer agent unavailable',
      message: error.message,
    };
  }
};

const testNetworkPrinterAgent = (config) => callLocalPrinterAgent('/printer/test', config);

const sendToNetworkPrinterAgent = (config, zpl) =>
  callLocalPrinterAgent('/printer/print', {
    ...config,
    zpl,
  });

const getNetworkPrinterHttpBaseUrl = (config = {}) => {
  const normalizedConfig = normalizeNetworkPrinterConfig(config);
  const httpPort =
    normalizedConfig.port && !['', DEFAULT_NETWORK_PRINTER_PORT, '9100'].includes(normalizedConfig.port)
      ? `:${normalizedConfig.port}`
      : '';

  return `http://${normalizedConfig.ip}${httpPort}`;
};

const buildHttpNetworkErrorMessage = (error) => {
  if (window.location.protocol === 'https:') {
    return 'Network printer HTTP blocked on HTTPS';
  }

  return error?.message || 'Could not connect to network printer';
};

const testNetworkPrinterHttp = async (config) => {
  try {
    await fetch(`${getNetworkPrinterHttpBaseUrl(config)}/`, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
    });

    return {
      ok: true,
      success: true,
      message: 'Network printer connection successful',
    };
  } catch (error) {
    return {
      ok: false,
      error: true,
      errorMsg: buildHttpNetworkErrorMessage(error),
    };
  }
};

const sendToNetworkPrinterHttp = async (config, zpl) => {
  try {
    await fetch(`${getNetworkPrinterHttpBaseUrl(config)}/pstprnt`, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8',
      },
      body: zpl,
    });

    return {
      ok: true,
      success: true,
      message: 'ZPL sent to network printer',
    };
  } catch (error) {
    return {
      ok: false,
      error: true,
      errorMsg: buildHttpNetworkErrorMessage(error),
    };
  }
};

const sendToBrowserPrint = (zplContent, device) =>
  new Promise((resolve, reject) => {
    const handlePrinter = (printer) => {
      if (!printer || typeof printer.send !== 'function') {
        reject(new Error('No se detectó impresora compatible.'));
        return;
      }

      printer.send(
        zplContent,
        () => resolve(),
        (error) => {
          reject(new Error(`Error al imprimir: ${error.message}`));
        },
      );
    };

    if (device && typeof device.send === 'function') {
      handlePrinter(device);
      return;
    }

    if (typeof window.BrowserPrint === 'undefined') {
      reject(new Error('BrowserPrint not available Zebra'));
      return;
    }

    window.BrowserPrint.getDefaultDevice(
      'printer',
      (printer) => handlePrinter(printer),
      (error) => {
        reject(new Error(`No se detectó impresora: ${error.message}`));
      },
    );
  });

const exportZplAsPdf = async (zplContent, filePrefix = 'qr-labels') => {
  const response = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
    method: 'POST',
    headers: {
      Accept: 'application/pdf',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: zplContent,
  });

  if (!response.ok) {
    throw new Error(`Labelary HTTP ${response.status}`);
  }

  const pdfBlob = await response.blob();
  const fileUrl = window.URL.createObjectURL(pdfBlob);
  const anchor = document.createElement('a');
  anchor.href = fileUrl;
  anchor.download = getPdfFileName(filePrefix);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(fileUrl);
};

function* offerQrPdfFallback(
  zplContent,
  filePrefix,
  allowEnableMode = false,
  silentWhenEnabled = false,
) {
  if (!zplContent) return;
  const normalizedZpl = Array.isArray(zplContent) ? zplContent.join('\n') : `${zplContent}`;
  if (!normalizedZpl.trim()) return;

  const contingencyEnabled = isPdfContingencyModeEnabled();
  if (!silentWhenEnabled || !contingencyEnabled) {
    const wantsPdf = askExportQrPdf();
    if (!wantsPdf) return;
  }

  try {
    yield call(exportZplAsPdf, normalizedZpl, filePrefix);
    if (allowEnableMode && !contingencyEnabled && askEnablePdfContingencyMode()) {
      setPdfContingencyMode(true);
      yield put(appActions.setPdfMode(true));
      yield put(appActions.setSuccess(t('pdf_mode_on')));
    }
    yield put(appActions.setSuccess(t('success_label_pdf')));
  } catch (error) {
    yield put(appActions.setError(t('error_label_pdf', { message: error.message })));
  }
}

function connect() {
  console.log({ saga: true }, '===> Try connect');
}

function subscribe(socket) {
  return eventChannel((emit) => {
    socket.on('objects', (e) => {
      if (truthty(e.objects)) {
        emit(appActions.objectsFromSocketReceived({ objects: e.objects }));
      }
      if (truthty(e.ids)) {
        emit(appActions.deleteObjectsFromSocketReceived({ response: { ids: e.ids } }));
      }
    });
    return () => {};
  });
}

function* read(socket) {
  let action;
  const channel = yield call(subscribe, socket);
  while (true) {
    action = yield take(channel);
    yield put(action);
  }
}

function* flow() {
  if (process.env.NODE_ENV === 'production') {
    while (true) {
      yield take(ACTIVATE);
    }
  }
}

function* logout() {
  yield takeLatest(LOG_OUT, function* () {
    yield apiRequest('/logout', { method: 'get' });
  });
}

function* searchPrinter() {
  yield takeLatest(SEARCH_PRINTER, function* () {
    const printerMode = yield select((state) => state.app.printerMode);
    const networkPrinterConfig = yield select((state) => state.app.networkPrinterConfig);

    if (printerMode === 'network-ip') {
      const validation = validateNetworkPrinterConfig(networkPrinterConfig);

      if (!validation.valid) {
        yield put({
          type: SEARCH_PRINTER_FAIL,
          msg: validation.message,
        });
        return;
      }

      yield put(
        appActions.setNetworkPrinterStatus({
          state: 'validating',
          message: 'Validating network printer',
        }),
      );

      try {
        let response = yield call(testNetworkPrinterHttp, validation.config);

        if (response?.error || response?.ok === false) {
          response = yield call(testNetworkPrinterAgent, validation.config);
        }

        if (response?.error || response?.ok === false) {
          throw new Error(
            response?.errorMsg || response?.message || 'Could not connect to network printer',
          );
        }

        setPdfContingencyMode(false);
        yield put(appActions.setPdfMode(false));
        yield put(appActions.setSuccess('Network printer connected'));
        yield put({
          type: SEARCH_PRINTER_SUCCESS,
          device: buildNetworkPrinterDevice(validation.config),
        });
      } catch (error) {
        yield put({
          type: SEARCH_PRINTER_FAIL,
          msg: error?.message || 'Could not connect to network printer',
        });
      }

      return;
    }

    let device = null;
    let connectionType = null;
    const supportsUsb = !!window?.navigator?.usb;

    if (supportsUsb) {
      try {
        const devices = yield window.navigator.usb.getDevices();
        if (devices.length === 0 || !devices.find((d) => d.vendorId === PRINTER_VENDOR_ID)) {
          try {
            device = yield window.navigator.usb.requestDevice({
              filters: [{ vendorId: PRINTER_VENDOR_ID }],
            });
          } catch (e) {
            console.log('Permiso USB denegado o cancelado:', e.message);
          }
        } else {
          device = devices.find((d) => d.vendorId === PRINTER_VENDOR_ID);
        }

        if (device) {
          try {
            yield device.open();
            yield device.selectConfiguration(1);
            yield device.claimInterface(0);
            connectionType = 'usb';
          } catch (e) {
            console.log('Error al abrir dispositivo USB:', e.message);
          }
        }
      } catch (e) {
        console.log('Error accediendo a USB:', e.message);
      }
    }
    let browserPrintDevice = null;
    if (typeof window.BrowserPrint !== 'undefined') {
      try {
        browserPrintDevice = yield call(resolveBrowserPrintPrinter);

        if (!device && browserPrintDevice) {
          setPdfContingencyMode(false);
          yield put(appActions.setPdfMode(false));
          yield put(appActions.setSuccess('Printer connected via network or Bluetooth'));
          device = browserPrintDevice;
          connectionType = browserPrintDevice.connection || 'network';
        }
      } catch (e) {
        console.log('Error buscando por BrowserPrint:', e.message);
      }
    }

    if (device) {
      setPdfContingencyMode(false);
      yield put(appActions.setPdfMode(false));
      yield put({
        type: SEARCH_PRINTER_SUCCESS,
        device,
        connectionType,
      });
    } else {
      yield put({
        type: SEARCH_PRINTER_FAIL,
        msg: 'No connected printers found',
      });
    }
  });
}

function* printLabel() {
  yield takeLatest(PRINT_LABEL, function* (action) {
    const printerMode = yield select((state) => state.app.printerMode);
    const networkPrinterConfig = yield select((state) => state.app.networkPrinterConfig);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
    let printSuccess = false;

    const response = yield apiRequest(`api/v1/lots/generateLabels`, {
      method: 'post',
      body: JSON.stringify(action.labelData),
    });

    if (response.error) {
      yield put(appActions.setError('Error generating labels'));
      return;
    }

    yield put(appActions.setSuccess('Labels generated'));

    if (isPdfContingencyModeEnabled()) {
      yield call(offerQrPdfFallback, response.lotLabels, 'lot-labels', false, true);
      return;
    }

    if (printerMode === 'network-ip') {
      const validation = validateNetworkPrinterConfig(networkPrinterConfig);

      if (!validation.valid) {
        yield put(appActions.setError(validation.message));
        yield call(offerQrPdfFallback, response.lotLabels, 'lot-labels', true);
        return;
      }

      try {
        yield put(appActions.setLoader('printing', true));
        let printResponse = yield call(sendToNetworkPrinterHttp, validation.config, response.lotLabels);

        if (printResponse?.error || printResponse?.ok === false) {
          printResponse = yield call(sendToNetworkPrinterAgent, validation.config, response.lotLabels);
        }

        yield put(appActions.setLoader('printing', false));

        if (printResponse?.error || printResponse?.ok === false) {
          throw new Error(
            printResponse?.errorMsg || printResponse?.message || 'Could not print check connection',
          );
        }
      } catch (error) {
        yield put(appActions.setLoader('printing', false));
        yield put(appActions.setError(error?.message || 'Could not print check connection'));
        yield call(offerQrPdfFallback, response.lotLabels, 'lot-labels', true);
        return;
      }

      yield put(appActions.setSuccess('Printing completed via IP'));
      return;
    }

    if (!isMobile && action.device && action.device.opened) {
      try {
        const encoder = new TextEncoder();
        const data = encoder.encode(response.lotLabels);

        yield put(appActions.setLoader('printing', true));
        const res = yield action.device.transferOut(1, data);
        yield put(appActions.setLoader('printing', false));

        if (res.status === 'ok') {
          yield put(appActions.setSuccess('Printing completed via USB'));
          printSuccess = true;
        }
      } catch (e) {
        console.log('Error WebUSB, intentando BrowserPrint:', e);
        yield put(appActions.setError('USB failed, trying via network'));
      }
    }

    if (!printSuccess) {
      try {
        yield put(appActions.setLoader('printing', true));
        yield call(sendToBrowserPrint, response.lotLabels, action.device);
        yield put(appActions.setLoader('printing', false));
        yield put(
          appActions.setSuccess(
            isMobile ? 'Printing completed via Bluetooth' : 'Printing completed via network',
          ),
        );
        printSuccess = true;
      } catch (e) {
        yield put(appActions.setLoader('printing', false));
        console.log('Error al imprimir por BrowserPrint:', e);
        yield put(appActions.setError(`Error en BrowserPrint: ${e.message}`));
      }
    }

    if (!printSuccess) {
      yield put(appActions.setError('Could not print check connection'));
      yield call(offerQrPdfFallback, response.lotLabels, 'lot-labels', true);
    }
  });
}

function* printReceptionLabel() {
  yield takeLatest(PRINT_RECEPTION_LABEL, function* (action) {
    const printerMode = yield select((state) => state.app.printerMode);
    const networkPrinterConfig = yield select((state) => state.app.networkPrinterConfig);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

    let printSuccess = false;
    let zplToPrint = action.zpl;
    const allowPdfFallback = Boolean(action.allowPdfFallback);

    if (!zplToPrint && action.labelData) {
      const response = yield apiRequest(`api/v1/lots/generateReceiptLabel`, {
        method: 'post',
        body: JSON.stringify(action.labelData),
      });

      if (response.error) {
        yield put(appActions.setError('Error generating labels'));
        return;
      }

      zplToPrint = response.lotLabels;
      yield put(appActions.setSuccess('Labels generated'));
    }

    if (isPdfContingencyModeEnabled()) {
      yield call(offerQrPdfFallback, zplToPrint, 'receipt-qr-labels', false, true);
      return;
    }

    if (!zplToPrint) {
      yield put(appActions.setError('No ZPL data to print'));
      return;
    }

    if (printerMode === 'network-ip') {
      const validation = validateNetworkPrinterConfig(networkPrinterConfig);

      if (!validation.valid) {
        yield put(appActions.setError(validation.message));
        if (allowPdfFallback) {
          yield call(offerQrPdfFallback, zplToPrint, 'receipt-qr-labels', true);
        }
        return;
      }

      try {
        yield put(appActions.setLoader('printing', true));
        let printResponse = yield call(sendToNetworkPrinterHttp, validation.config, zplToPrint);

        if (printResponse?.error || printResponse?.ok === false) {
          printResponse = yield call(sendToNetworkPrinterAgent, validation.config, zplToPrint);
        }

        yield put(appActions.setLoader('printing', false));

        if (printResponse?.error || printResponse?.ok === false) {
          throw new Error(
            printResponse?.errorMsg || printResponse?.message || 'Could not print check connection',
          );
        }
      } catch (error) {
        yield put(appActions.setLoader('printing', false));
        yield put(appActions.setError(error?.message || 'Could not print check connection'));
        if (allowPdfFallback) {
          yield call(offerQrPdfFallback, zplToPrint, 'receipt-qr-labels', true);
        }
        return;
      }

      yield put(appActions.setSuccess('Printing completed via IP'));
      return;
    }

    if (!isMobile && action.device && action.device.opened) {
      try {
        const encoder = new TextEncoder();
        const data = encoder.encode(zplToPrint);

        yield put(appActions.setLoader('printing', true));
        const res = yield action.device.transferOut(1, data);
        yield put(appActions.setLoader('printing', false));

        if (res.status === 'ok') {
          yield put(appActions.setSuccess('Printing completed via USB'));
          printSuccess = true;
        }
      } catch (e) {
        console.log('Error WebUSB:', e);
      }
    }

    if (!printSuccess) {
      try {
        yield put(appActions.setLoader('printing', true));
        yield call(sendToBrowserPrint, zplToPrint, action.device);
        yield put(appActions.setLoader('printing', false));
        yield put(
          appActions.setSuccess(
            isMobile ? 'Printing completed via Bluetooth' : 'Printing completed via network',
          ),
        );
        printSuccess = true;
      } catch (e) {
        yield put(appActions.setLoader('printing', false));
        yield put(appActions.setError(`Error en BrowserPrint: ${e.message}`));
      }
    }

    if (!printSuccess) {
      yield put(appActions.setError('Could not print check connection'));
      yield call(offerQrPdfFallback, zplToPrint, 'receipt-qr-labels', true);
    }
  });
}

function* printLabelPaks() {
  yield takeLatest(PRINT_LABEL_PAKS, function* (action) {
    const printerMode = yield select((state) => state.app.printerMode);
    const networkPrinterConfig = yield select((state) => state.app.networkPrinterConfig);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
    let printSuccess = false;
    const zplToPrint = action.labelData.lotIds;

    if (isPdfContingencyModeEnabled()) {
      yield call(offerQrPdfFallback, zplToPrint, 'pak-qr-labels', false, true);
      return;
    }

    if (printerMode === 'network-ip') {
      const validation = validateNetworkPrinterConfig(networkPrinterConfig);

      if (!validation.valid) {
        yield put(appActions.setError(validation.message));
        yield call(offerQrPdfFallback, zplToPrint, 'pak-qr-labels', true);
        return;
      }

      yield put(appActions.setSuccess('Labels generated'));
      try {
        yield put(appActions.setLoader('printing', true));
        let printResponse = yield call(sendToNetworkPrinterHttp, validation.config, zplToPrint);

        if (printResponse?.error || printResponse?.ok === false) {
          printResponse = yield call(sendToNetworkPrinterAgent, validation.config, zplToPrint);
        }

        yield put(appActions.setLoader('printing', false));

        if (printResponse?.error || printResponse?.ok === false) {
          throw new Error(
            printResponse?.errorMsg || printResponse?.message || 'Could not print check connection',
          );
        }
      } catch (error) {
        yield put(appActions.setLoader('printing', false));
        yield put(appActions.setError(error?.message || 'Could not print check connection'));
        yield call(offerQrPdfFallback, zplToPrint, 'pak-qr-labels', true);
        return;
      }

      yield put(appActions.setSuccess('Printing completed via IP'));
      return;
    }

    if (!isMobile && action.device && action.device.opened) {
      try {
        yield put(appActions.setSuccess('Labels generated'));
        const encoder = new TextEncoder();
        const data = encoder.encode(action.labelData.lotIds);

        yield put(appActions.setLoader('printing', true));
        const res = yield action.device.transferOut(1, data);
        yield put(appActions.setLoader('printing', false));

        if (res.status === 'ok') {
          yield put(appActions.setSuccess('Printing completed via USB'));
          printSuccess = true;
        } else {
          yield put(appActions.setError('USB printing failed'));
        }
      } catch (e) {
        console.log('Error WebUSB en printLabelPaks:', e);
        yield put(appActions.setError('Error printing via USB'));
      }
    }

    if (!printSuccess) {
      try {
        yield put(appActions.setSuccess('Labels generated'));
        yield put(appActions.setLoader('printing', true));
        yield call(sendToBrowserPrint, action.labelData.lotIds, action.device);
        yield put(appActions.setLoader('printing', false));
        yield put(
          appActions.setSuccess(
            isMobile ? 'Printing completed via Bluetooth' : 'Printing completed via network',
          ),
        );
        printSuccess = true;
      } catch (e) {
        yield put(appActions.setLoader('printing', false));
        yield put(appActions.setError(`Error en BrowserPrint: ${e.message}`));
      }
    }

    if (!printSuccess) {
      yield put(appActions.setError('Could not print check connection'));
      yield call(offerQrPdfFallback, zplToPrint, 'pak-qr-labels', true);
    }
  });
}

function* searchWeightScale() {
  yield takeLatest(SEARCH_SCALE, function* () {
    const devices = yield window.navigator.usb.getDevices();

    let device;

    if (
      devices.length === 0 ||
      !devices.find((d) => d.vendorId === WEIGHT_SCALE_VENDOR_ID || d.vendorId === vendprId2)
    ) {
      try {
        device = yield window.navigator.usb.requestDevice({
          filters: [{ vendorId: WEIGHT_SCALE_VENDOR_ID }, { vendorId: vendprId2 }],
        });
      } catch (e) {
        yield put({
          type: SEARCH_SCALE_FAIL,
          msg: 'Grant permissions for scale',
        });
      }
    } else {
      device = devices.find(
        (d) => d.vendorId === WEIGHT_SCALE_VENDOR_ID || d.vendorId === vendprId2,
      );
    }

    if (device) {
      try {
        yield put({
          type: SEARCH_SCALE_SUCCESS,
          device,
        });
      } catch (e) {
        yield put({
          type: SEARCH_SCALE_FAIL,
          msg: 'Scale is in use',
        });
      }
    } else {
      yield put({
        type: SEARCH_SCALE_FAIL,
        msg: 'No connected scales found',
      });
    }
  });
}

function* readScale() {
  yield takeLatest(SEARCH_SCALE_SUCCESS, function* (action) {
    const { device } = action;
    if (truthty(device.productId)) {
      let reading = true;
      window.navigator.usb.addEventListener('disconnect', (event) => {
        device.close();
        reading = false;
      });

      yield device.open();
      yield device.selectConfiguration(1);

      try {
        yield device.claimInterface(device.configuration.interfaces[0].interfaceNumber);
        yield vendorRead(device, 0x8484, 0);
        yield vendorWrite(device, 0x0404, 0);
        yield vendorRead(device, 0x8484, 0);
        yield vendorRead(device, 0x8383, 0);
        yield vendorRead(device, 0x8484, 0);
        yield vendorWrite(device, 0x0404, 1);
        yield vendorRead(device, 0x8484, 0);
        yield vendorRead(device, 0x8383, 0);
        yield vendorWrite(device, 0, 1);
        yield vendorWrite(device, 1, 0);
        yield vendorWrite(device, 2, 0x44);
        yield setBaudrate(device);
      } catch (error) {
        console.log('error => ', error);
      }

      const weight = 0;
      while (reading) {
        yield delay(100);
        let decoder;
        try {
          const newWeight = yield device
            .transferIn(3, 2048)
            .then((result) => {
              decoder = new TextDecoder();
              const decodedResult = decoder.decode(result.data);
              const w = parseFloat(decodedResult.match(/.......kg/)[0].split('kg')[0]);
              return w;
            })
            .catch((error) => {
              console.log('error', error);
              return weight;
            });
          yield put({
            type: CAPTURE_WEIGHT,
            scaleWeight: newWeight,
          });
        } catch (e) {
          console.error('error', e);
        }
      }
      yield put({
        type: READ_SCALE_SUCCESS,
      });
      device.close();
    } else {
      yield put({
        type: READ_SCALE_FAIL,
        msg: 'No scale detected',
      });
    }
  });
}

function* editWeightScale() {
  yield takeLatest(EDIT_WEIGHT_SCALE, function* (action) {
    const { scaleWeight, fnc } = action;
    fnc(scaleWeight);
  });
}

function* getUser() {
  yield takeLatest(GET_USER, function* () {
    const response = yield apiRequest('api/v1/users/user', { method: 'get' }, true);
    if (truthty(response)) {
      yield put(apiSuccess(GET_USER_SUCCESS, { user: response.User, role: response.Role }));
    } else {
      yield put(apiSuccess(GET_USER_FAIL));
    }
  });
}
function* getStateFromApi() {
  yield takeLatest(GET_STATE_FROM_API, function* () {
    const response = yield apiRequest('api/v1/app', { method: 'get' }, true);
    if (truthty(response)) {
      yield put(apiSuccess(GET_STATE_FROM_API_SUCCESS, response));
    }
  });
}

function* exportExcel() {
  yield takeLatest(EXPORT_EXCEL, function* (action) {
    yield put(appActions.setLoader('export-dispatches', true));
    const query = objectToQueryString({ ...action.filters, socketId: action.socketId });
    const response = yield apiRequest(`api/v1/${action.path}?${query}`, { method: 'get' }, false);
    if (response?.error) {
      yield put(appActions.setError(response.errorMsg || 'Error exporting file'));
      yield put(appActions.exportingExcel());
      yield put(appActions.toggleExportMsg());
      yield put(appActions.setLoader('export-dispatches', false));
      return;
    }
    if (response?.result) {
      yield put(appActions.exportExcelReady(response.result));
      yield put(appActions.exportingExcel());
    }
    yield put(apiSuccess(EXPORT_EXCEL_SUCCESS, response));
    yield put(appActions.setLoader('export-dispatches', false));
  });
}

export default function* root() {
  yield spawn(searchWeightScale);
  yield spawn(searchPrinter);
  yield spawn(readScale);
  yield spawn(getUser);
  yield spawn(logout);
  yield spawn(printLabel);
  yield spawn(printReceptionLabel);
  yield spawn(printLabelPaks);
  yield spawn(flow);
  yield spawn(getStateFromApi);
  yield spawn(exportExcel);
  yield spawn(editWeightScale);
}
